import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import PortfolioComposite from "../../../api/models/portfolioComposite";
import { plainToInstance } from "class-transformer";
import Portfolio from "../../../api/models/portfolio";
import Position from "../../../api/models/portfolioPosition";

interface PortfolioDetailState {
  compositeSelected: object | undefined
  compositeSelectedPrevious: object | undefined
}

const initialState: PortfolioDetailState = {
  compositeSelected: undefined,
  compositeSelectedPrevious: undefined
}

const portfolioDetailSlice = createSlice({
  name: 'portfolioDetail',
  initialState,
  reducers: {
    setSelectedComposite(state, action: PayloadAction<object | undefined>) {
      state.compositeSelectedPrevious = state.compositeSelected;
      state.compositeSelected = action.payload;
    }
  }
})


// Export selectors
export const selectCompositeSelected = createSelector([
  (state: RootState) => state.portfolios.portfolios,
  (state: RootState) => state.portfolioDetail.compositeSelected
], (portfolios, compositeSelected) => {
  if (compositeSelected) {
    return plainToInstance(PortfolioComposite, compositeSelected);
  } else if (portfolios && portfolios.length > 0) {
    const portfolio = plainToInstance(Portfolio, portfolios[0]);
    return plainToInstance(PortfolioComposite, portfolio.portfolioComposite);
  }
})

export const selectChildrenComposites = createSelector([
  (state: RootState) => state.portfolios.portfolios,
  (state: RootState) => state.portfolioDetail.compositeSelected
], (portfolios, compositeSelected) => {
  let selected: PortfolioComposite | undefined = undefined;
  if (compositeSelected) {
    selected = plainToInstance(PortfolioComposite, compositeSelected);
  } else if (portfolios && portfolios.length > 0) {
    const portfolio = plainToInstance(Portfolio, portfolios[0]);
    selected = plainToInstance(PortfolioComposite, portfolio.portfolioComposite);
  }
  if (!selected) {
    return undefined;
  }
  const children = selected.portfolioComponents.filter(component => component instanceof PortfolioComposite);
  return plainToInstance(PortfolioComposite, children);
})

/** Returns all positions that belongs to the selected composite and any composite below it, recursively. */
export const selectChildrenPositionRecursively = createSelector([
  selectCompositeSelected
], (compositeSelected) => {
  const selected = plainToInstance(PortfolioComposite, compositeSelected);
  if (selected) {
    const children = selected.getAllPositions();
    children.sort((a, b) => a.absoluteDeviation() > b.absoluteDeviation() ? -1 : 1);
    return plainToInstance(Position, children);
  } else {
    return [];
  }
})

export const selectStatus = (state: RootState) => state.portfolios.status;

// Export the auto-generated action creator with the same name
export const { setSelectedComposite } = portfolioDetailSlice.actions;

// Export the generated reducer function
export default portfolioDetailSlice.reducer;

